import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { format, parseISO, addDays, addMinutes, isBefore, isAfter, isSameDay, startOfWeek, addWeeks } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import { API_BASE_URL } from '@/lib/api-client';
import type { Service, Provider, AvailableSlot, CustomAnswers } from '@/types/booking';
import type { Organization } from '@/types/organization';
import { getSubdomain, isSubdomainSite, getMainAppUrl } from '@/utils/subdomain';
import { normalizeSlot, type NormalizedSlot } from '@/utils/normalizeSlot';
import {
  Package,
  Calendar,
  Mail,
  CheckCircle,
} from 'lucide-react';

// Type for customer form data
export type CustomerFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  notes?: string;
};

// Create customer schema with translations
const createCustomerSchema = (
  requirePhone: boolean,
  translations: {
    firstNameRequired: string;
    lastNameRequired: string;
    emailRequired: string;
    emailInvalid: string;
    phoneRequired: string;
    phoneInvalid: string;
    notesTooLong: string;
  }
) => z.object({
  firstName: z.string().min(1, translations.firstNameRequired),
  lastName: z.string().min(1, translations.lastNameRequired),
  email: z.string().min(1, translations.emailRequired).email(translations.emailInvalid),
  phone: requirePhone
    ? z.string()
      .min(1, translations.phoneRequired)
      .refine(
        (val) => isValidPhoneNumber(val),
        translations.phoneInvalid
      )
    : z.string()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => !val || val === '' || isValidPhoneNumber(val),
        translations.phoneInvalid
      ),
  notes: z.string().max(500, translations.notesTooLong).optional(),
});

export interface BookingStep {
  id: string;
  title: string;
  icon: typeof Package;
}

export function useBookingFlow() {
  const params = useParams<{ slug?: string }>();

  // Support both subdomain-based (org.slotyapp.com) and slug-based (/org/book) access
  const subdomain = getSubdomain();
  const orgSlug = subdomain || params.slug;

  const { t } = useTranslation(['publicBooking', 'validation']);

  // Booking steps - using translation
  const BOOKING_STEPS: BookingStep[] = [
    { id: 'service', title: t('publicBooking:bookingSteps.selectService'), icon: Package },
    { id: 'datetime', title: t('publicBooking:bookingSteps.chooseDateTime'), icon: Calendar },
    { id: 'details', title: t('publicBooking:bookingSteps.yourInformation'), icon: Mail },
    { id: 'confirmation', title: t('publicBooking:bookingSteps.confirmation'), icon: CheckCircle },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSavingBooking, setIsSavingBooking] = useState(false);

  // Organization data
  const [organization, setOrganization] = useState<Organization | null>(null);

  // Booking state
  const [services, setServices] = useState<Service[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [availableSlots, setAvailableSlots] = useState<NormalizedSlot[]>([]);
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [hasFutureSlots, setHasFutureSlots] = useState<boolean | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedSlot, setSelectedSlot] = useState<NormalizedSlot | null>(null);
  const [bookingConfirmation, setBookingConfirmation] = useState<any>(null);
  const [customAnswers, setCustomAnswers] = useState<CustomAnswers>({});

  const { toast } = useToast();
  // Use organization currency if available, otherwise use USD to prevent auth calls
  const { formatCurrency } = useCurrency((organization?.currency || 'USD') as any);

  // --- URL-based step tracking ---
  const updateStep = useCallback((newStep: number) => {
    setCurrentStep(newStep);
    const url = new URL(window.location.href);
    url.searchParams.set('step', String(newStep));
    window.history.pushState({ step: newStep }, '', url.toString());
  }, []);

  // On mount, read step from URL and restore sessionStorage selections
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlStep = parseInt(params.get('step') || '0', 10);
    if (urlStep >= 0 && urlStep <= 3) {
      setCurrentStep(urlStep);
    }
    // Set initial URL state without creating a history entry
    const url = new URL(window.location.href);
    url.searchParams.set('step', String(urlStep >= 0 && urlStep <= 3 ? urlStep : 0));
    window.history.replaceState({ step: urlStep >= 0 && urlStep <= 3 ? urlStep : 0 }, '', url.toString());
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const step = event.state?.step ?? 0;
      if (step >= 0 && step <= 3) {
        setCurrentStep(step);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // --- sessionStorage persistence for selections ---
  useEffect(() => {
    if (selectedService) {
      sessionStorage.setItem('booking_service', JSON.stringify(selectedService.id));
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedDate) {
      sessionStorage.setItem('booking_date', format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedSlot) {
      sessionStorage.setItem('booking_slot', JSON.stringify(selectedSlot));
    }
  }, [selectedSlot]);

  // Restore selections from sessionStorage after services load
  useEffect(() => {
    if (services.length === 0) return;

    const savedServiceId = sessionStorage.getItem('booking_service');
    const savedDate = sessionStorage.getItem('booking_date');
    const savedSlot = sessionStorage.getItem('booking_slot');

    if (savedServiceId) {
      const serviceId = JSON.parse(savedServiceId);
      const matchingService = services.find((s) => s.id === serviceId);
      if (matchingService && !selectedService) {
        setSelectedService(matchingService);
      }
    }

    if (savedDate) {
      const parsedDate = parseISO(savedDate);
      if (!isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate);
        setCurrentWeekStart(startOfWeek(parsedDate, { weekStartsOn: 1 }));
      }
    }

    if (savedSlot) {
      try {
        const parsedSlot = JSON.parse(savedSlot);
        if (!selectedSlot) {
          setSelectedSlot(parsedSlot);
        }
      } catch {
        // ignore invalid JSON
      }
    }
  }, [services]);

  // Clear sessionStorage on booking completion or "Book Another"
  const clearBookingStorage = useCallback(() => {
    sessionStorage.removeItem('booking_service');
    sessionStorage.removeItem('booking_date');
    sessionStorage.removeItem('booking_slot');
  }, []);

  // Create schema with translated validation messages
  const requirePhone = organization?.booking_settings?.require_phone ?? false;
  const customerSchema = useMemo(() => createCustomerSchema(
    requirePhone,
    {
      firstNameRequired: t('publicBooking:validation.firstNameRequired', { defaultValue: 'First name is required' }),
      lastNameRequired: t('publicBooking:validation.lastNameRequired', { defaultValue: 'Last name is required' }),
      emailRequired: t('publicBooking:validation.emailRequired', { defaultValue: 'Email is required' }),
      emailInvalid: t('publicBooking:validation.emailInvalid', { defaultValue: 'Please enter a valid email address' }),
      phoneRequired: t('publicBooking:validation.phoneRequired', { defaultValue: 'Phone number is required' }),
      phoneInvalid: t('publicBooking:validation.phoneInvalid', { defaultValue: 'Please enter a valid phone number' }),
      notesTooLong: t('publicBooking:validation.notesTooLong', { defaultValue: 'Notes cannot exceed 500 characters' }),
    }
  ), [t, requirePhone]);

  const customerForm = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: '',
    },
  });

  // Load organization and services on mount
  useEffect(() => {
    loadOrganizationData();
  }, [orgSlug]);

  // Load providers when service is selected
  useEffect(() => {
    if (selectedService) {
      loadProvidersForService();
    }
  }, [selectedService]);

  // Load available slots when date and service are selected
  useEffect(() => {
    if (selectedService && selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedService, selectedDate, selectedProvider]);

  // Load week slots when week changes or service/provider changes
  useEffect(() => {
    if (selectedService) {
      loadWeekSlots();
    }
  }, [selectedService, currentWeekStart, selectedProvider]);

  // Auto-navigate to first available date when service is selected
  // Use a ref to track which service we've already auto-selected for (prevents duplicate calls)
  const autoSelectedForServiceRef = useRef<number | null>(null);

  useEffect(() => {
    // Only auto-select once per service selection
    if (selectedService && organization && autoSelectedForServiceRef.current !== selectedService.id) {
      autoSelectedForServiceRef.current = selectedService.id;
      findFirstAvailableDate();
    }
  }, [selectedService, organization]);

  const loadOrganizationData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingServices(true);

      // Load organization details
      const orgResponse = await fetch(`${API_BASE_URL}/v1/organizations/${orgSlug}`);
      if (!orgResponse.ok) throw new Error('Organization not found');
      const orgData = await orgResponse.json();
      setOrganization(orgData.data);

      // Load services
      const servicesResponse = await fetch(`${API_BASE_URL}/v1/services?organization_id=${orgData.data.id}`);
      if (!servicesResponse.ok) throw new Error('Failed to load services');
      const servicesData = await servicesResponse.json();
      setServices(servicesData.data.filter((s: Service) => s.active));
    } catch (error) {
      console.error('Failed to load organization data:', error);

      // If org not found and accessed via subdomain, redirect to landing page
      if (isSubdomainSite()) {
        window.location.href = getMainAppUrl();
        return;
      }

      toast({
        title: t('publicBooking:errors.loadingFailed'),
        description: t('publicBooking:errors.loadingFailedMessage'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsLoadingServices(false);
    }
  };

  const loadProvidersForService = async () => {
    if (!selectedService || !organization) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/providers?organization_id=${organization.id}&service_id=${selectedService.id}`);
      if (!response.ok) throw new Error('Failed to load providers');
      const data = await response.json();
      setProviders(data.data.filter((p: Provider) => p.active));
    } catch (error) {
      console.error('Failed to load providers:', error);
      setProviders([]);
    }
  };

  // Several slot requests can be in flight (today, then the first free day
  // once findFirstAvailableDate answers). Only the latest one may write.
  const slotsRequestRef = useRef(0);

  const loadAvailableSlots = async () => {
    if (!selectedService || !organization) return;
    const requestId = ++slotsRequestRef.current;
    const isStale = () => requestId !== slotsRequestRef.current;

    try {
      // Only show skeleton on initial load, not when switching dates
      if (availableSlots.length === 0) {
        setIsLoadingSlots(true);
      }
      const params = new URLSearchParams({
        organization_id: organization.id.toString(),
        service_id: selectedService.id.toString(),
        date: format(selectedDate, 'yyyy-MM-dd'),
      });

      if (selectedProvider) {
        params.append('provider_id', selectedProvider.id.toString());
      }

      // Try the new availability-based API first
      let response = await fetch(`${API_BASE_URL}/v1/availability/slots?${params}`);
      if (isStale()) return;

      if (response.ok) {
        // New availability-based system
        const data = await response.json();
        if (isStale()) return;
        // Transform to match AvailableSlot format before normalizing
        const rawSlots = data.data.map((slot: any) => ({
          id: `${slot.provider_id}-${slot.start_time}`,
          start_time: slot.start_time,
          end_time: slot.end_time,
          available: true,
          provider: slot.provider_name ? {
            id: slot.provider_id,
            displayName: slot.provider_name,
            public_id: slot.provider_public_id,
          } : null,
          provider_id: slot.provider_id,
          service_id: slot.service_id,
          duration_minutes: slot.duration_minutes,
          // Group-class capacity counters — preserved so the slot picker can
          // show "8/10 spots" for classes. Undefined for 1:1 services.
          max_capacity: slot.max_capacity,
          current_bookings: slot.current_bookings,
          spots_remaining: slot.spots_remaining,
          is_full: slot.is_full,
          waitlist_enabled: slot.waitlist_enabled,
        }));
        const normalizedSlots = rawSlots.map(normalizeSlot);
        // Sort by absolute time (Date comparison is robust to timezone-offset
        // format drift; localeCompare on ISO strings would be fragile). Stable
        // tie-break on providerId so multi-provider responses are deterministic.
        normalizedSlots.sort(
          (a: NormalizedSlot, b: NormalizedSlot) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime() ||
            (a.providerId ?? 0) - (b.providerId ?? 0)
        );
        setAvailableSlots(normalizedSlots);
      } else {
        // Fall back to legacy available_slots API
        response = await fetch(`${API_BASE_URL}/v1/available_slots?${params}`);
        if (isStale()) return;
        if (!response.ok) throw new Error('Failed to load available slots');
        const data = await response.json();
        if (isStale()) return;
        const available = data.data.filter((slot: AvailableSlot) => slot.available);
        const normalizedSlots = available.map(normalizeSlot);
        normalizedSlots.sort(
          (a: NormalizedSlot, b: NormalizedSlot) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime() ||
            (a.providerId ?? 0) - (b.providerId ?? 0)
        );
        setAvailableSlots(normalizedSlots);
      }
    } catch (error) {
      if (isStale()) return;
      console.error('Failed to load available slots:', error);
      setAvailableSlots([]);
    } finally {
      if (!isStale()) setIsLoadingSlots(false);
    }
  };

  const loadWeekSlots = async () => {
    if (!selectedService || !organization) return;

    try {
      const weekEnd = addDays(currentWeekStart, 6);
      const params = new URLSearchParams({
        organization_id: organization.id.toString(),
        service_id: selectedService.id.toString(),
        start_date: format(currentWeekStart, 'yyyy-MM-dd'),
        end_date: format(weekEnd, 'yyyy-MM-dd'),
      });

      if (selectedProvider) {
        params.append('provider_id', selectedProvider.id.toString());
      }

      // Try the new availability dates API first (more efficient)
      let response = await fetch(`${API_BASE_URL}/v1/availability/dates?${params}`);

      if (response.ok) {
        const data = await response.json();
        // New API returns available_dates array
        const datesSet = new Set<string>(data.data.available_dates || []);
        setDatesWithSlots(datesSet);
      } else {
        // Fall back to legacy available_slots API
        response = await fetch(`${API_BASE_URL}/v1/available_slots?${params}`);
        if (!response.ok) throw new Error('Failed to load week slots');
        const data = await response.json();

        // Create a set of dates that have available slots
        const datesSet = new Set<string>();
        data.data.forEach((slot: AvailableSlot) => {
          if (slot.available) {
            const slotDate = parseISO(slot.startTime || slot.start_time || '');
            datesSet.add(format(slotDate, 'yyyy-MM-dd'));
          }
        });
        setDatesWithSlots(datesSet);
      }
    } catch (error) {
      console.error('Failed to load week slots:', error);
      setDatesWithSlots(new Set());
    }
  };

  // Find and navigate to the first available date.
  //
  // How far ahead to look comes from the business's own settings
  // (`booking_horizon_days`, the highest advance_booking_days across its
  // providers, capped at the 90 days the endpoint accepts). It used to be a
  // hardcoded 30, which hid any provider whose next opening was further out:
  // a therapist whose next free day was 34 days away got "no free times",
  // while the API would have returned those slots. The fallback stays 30 for
  // an organization payload served before this field existed.
  const findFirstAvailableDate = async () => {
    if (!selectedService || !organization) return;

    try {
      const today = new Date();
      const horizonDays = Math.min(organization.booking_horizon_days || 30, 90);
      const endDate = addDays(today, horizonDays);

      const params = new URLSearchParams({
        organization_id: organization.id.toString(),
        service_id: selectedService.id.toString(),
        start_date: format(today, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });

      // Try new availability dates API first (more efficient)
      let response = await fetch(`${API_BASE_URL}/v1/availability/dates?${params}`);

      if (response.ok) {
        const data = await response.json();
        const availableDates = data.data.available_dates || [];

        if (availableDates.length > 0) {
          // Sort and get first available date
          availableDates.sort();
          const firstDateStr = availableDates[0];
          const firstDate = parseISO(firstDateStr);

          setSelectedDate(firstDate);
          setCurrentWeekStart(startOfWeek(firstDate, { weekStartsOn: 1 }));
          setHasFutureSlots(true);
        } else {
          setHasFutureSlots(false);
        }
      } else {
        // Fall back to legacy API
        response = await fetch(`${API_BASE_URL}/v1/available_slots?${params}`);
        if (!response.ok) return;

        const data = await response.json();
        const availableSlots = data.data.filter((slot: AvailableSlot) => slot.available);

        if (availableSlots.length > 0) {
          // Sort by start_time and get first
          availableSlots.sort((a: AvailableSlot, b: AvailableSlot) => {
            const aTime = parseISO(a.startTime || a.start_time || '');
            const bTime = parseISO(b.startTime || b.start_time || '');
            return aTime.getTime() - bTime.getTime();
          });

          const firstSlot = availableSlots[0];
          const firstDate = parseISO(firstSlot.startTime || firstSlot.start_time || '');

          setSelectedDate(firstDate);
          setCurrentWeekStart(startOfWeek(firstDate, { weekStartsOn: 1 }));
          setHasFutureSlots(true);
        } else {
          setHasFutureSlots(false);
        }
      }
    } catch (error) {
      console.error('Failed to find first available date:', error);
    }
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setSelectedProvider(null); // Reset provider selection
    setSelectedSlot(null); // Reset slot selection
    setHasFutureSlots(null); // Reset until findFirstAvailableDate resolves
    // Don't auto-advance - user must click Next
  };

  const handleProviderSelect = (provider: Provider | null) => {
    setSelectedProvider(provider);
    setSelectedSlot(null); // Reset slot selection when provider changes
  };

  const handleSlotSelect = (slot: NormalizedSlot) => {
    setSelectedSlot(slot);
    // Do NOT lock the provider filter to the clicked slot's provider:
    // the filter is sent to the API and would hide every other provider's
    // slots for the rest of the session. The provider is carried by the slot.
    // Don't auto-advance - user must click Next
  };

  // Validate custom questions - reusable function
  const validateCustomQuestions = (): boolean => {
    const customQuestions = selectedService?.custom_booking_questions || selectedService?.customBookingQuestions || [];

    for (const question of customQuestions) {
      if (question.required) {
        const answer = customAnswers[question.id];
        // Check for empty values: undefined, null, empty string, empty array
        const isEmpty = !answer ||
          answer === '' ||
          (typeof answer === 'string' && answer.trim() === '') ||
          (Array.isArray(answer) && answer.length === 0);

        if (isEmpty) {
          toast({
            title: t('publicBooking:errors.requiredField'),
            description: `${question.question} ${t('publicBooking:errors.isRequired')}`,
            variant: 'destructive',
          });
          return false;
        }
      }
    }
    return true;
  };

  const handleCustomerSubmit = async (data: CustomerFormData) => {
    // Validate phone if required by organization settings
    const requirePhone = organization?.booking_settings?.require_phone;
    if (requirePhone && (!data.phone || data.phone.trim() === '')) {
      toast({
        title: t('publicBooking:errors.requiredField'),
        description: t('publicBooking:validation.phoneRequired', { defaultValue: 'Phone number is required' }),
        variant: 'destructive',
      });
      return;
    }

    // Validate custom answers
    if (!validateCustomQuestions()) {
      return;
    }

    // Submit directly — the previous "Review your booking" modal gate was
    // removed (Step 20). Linked-slots warning is now shown inline above the
    // form so the customer reads it before clicking submit.
    await handleConfirmBooking();
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot || !organization || !selectedService) return;

    // Final validation before submitting
    if (!validateCustomQuestions()) {
      return;
    }

    const data = customerForm.getValues();

    try {
      setIsSavingBooking(true);

      // Check if this is an availability-based slot (has provider_id but no numeric id)
      // or a legacy slot-based booking (has numeric id)
      const isAvailabilityBased = typeof selectedSlot.id === 'string' && selectedSlot.providerId;

      let bookingData: any;

      if (isAvailabilityBased) {
        // New availability-based booking
        bookingData = {
          service_id: selectedService.id,
          provider_id: selectedSlot.providerId,
          start_time: selectedSlot.startTime,
          customer_email: data.email,
          customer_first_name: data.firstName,
          customer_last_name: data.lastName,
          customer_phone: data.phone,
          notes: data.notes,
          organization_id: organization.id,
          custom_answers: Object.keys(customAnswers).length > 0 ? customAnswers : undefined,
        };
      } else {
        // Legacy slot-based booking
        bookingData = {
          available_slot_id: selectedSlot.id,
          customer_email: data.email,
          customer_first_name: data.firstName,
          customer_last_name: data.lastName,
          customer_phone: data.phone,
          notes: data.notes,
          organization_id: organization.id,
          custom_answers: Object.keys(customAnswers).length > 0 ? customAnswers : undefined,
        };
      }

      const response = await fetch(`${API_BASE_URL}/v1/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok && response.status !== 202) throw new Error('Failed to create booking');

      const result = await response.json();
      const waitlisted = response.status === 202 && result?.data?.waitlisted === true;
      setBookingConfirmation(result.data);
      updateStep(3);
      clearBookingStorage();

      toast({
        title: waitlisted
          ? t('publicBooking:toasts.waitlistedTitle', "You're on the waitlist")
          : t('publicBooking:toasts.bookingSuccess'),
        description: waitlisted
          ? t('publicBooking:toasts.waitlistedMessage', "We'll email you when a spot opens up.")
          : t('publicBooking:toasts.bookingSuccessMessage'),
      });
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast({
        title: t('publicBooking:errors.bookingFailed'),
        description: t('publicBooking:errors.bookingFailedMessage'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  const generateGoogleCalendarUrl = () => {
    if (!selectedSlot || !selectedService) return '';
    const startTime = parseISO(selectedSlot.startTime || '');
    const duration = selectedService.duration || selectedService.duration_minutes || 60;
    const endTime = addMinutes(startTime, duration);
    const formatForCalendar = (d: Date) => format(d, "yyyyMMdd'T'HHmmss");
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: selectedService.name || 'Appointment',
      dates: `${formatForCalendar(startTime)}/${formatForCalendar(endTime)}`,
      details: `Booking at ${organization?.name || organization?.business_name || ''}`,
      location: organization?.address || '',
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  };

  const navigateStep = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentStep > 0) {
      updateStep(currentStep - 1);
    } else if (direction === 'next' && currentStep < BOOKING_STEPS.length - 1) {
      // Validate current step before proceeding
      if (currentStep === 0 && !selectedService) {
        toast({
          title: t('publicBooking:toasts.selectService'),
          variant: 'destructive',
        });
        return;
      }
      if (currentStep === 1 && !selectedSlot) {
        toast({
          title: t('publicBooking:toasts.selectTimeSlot'),
          variant: 'destructive',
        });
        return;
      }
      updateStep(currentStep + 1);
    }
  };

  return {
    // Translation
    t,

    // Organization
    orgSlug,
    organization,

    // Steps
    BOOKING_STEPS,
    currentStep,
    updateStep,
    navigateStep,

    // Loading states
    isLoading,
    isLoadingServices,
    isLoadingSlots,
    isSavingBooking,

    // Services
    services,
    selectedService,
    handleServiceSelect,

    // Providers
    providers,
    selectedProvider,
    handleProviderSelect,

    // Date/Time
    selectedDate,
    setSelectedDate,
    currentWeekStart,
    setCurrentWeekStart,
    datesWithSlots,
    hasFutureSlots,
    availableSlots,
    selectedSlot,
    handleSlotSelect,

    // Customer form
    customerForm,
    handleCustomerSubmit,
    customAnswers,
    setCustomAnswers,

    // Confirmation
    handleConfirmBooking,
    bookingConfirmation,
    generateGoogleCalendarUrl,
    clearBookingStorage,

    // Utilities
    formatCurrency,
    toast,
  };
}
